import { useEffect, useState } from 'react'
import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import {
	createAvitoAccount,
	getAvitoAccountErrorMessage,
	updateAvitoAccount,
	validateAvitoAccountInput,
} from '../../../utils/avitoAccounts'

function AvitoAccountFormDialog({ open, onClose, account = null, onSaved }) {
	const [name, setName] = useState('')
	const [status, setStatus] = useState('active')
	const [errors, setErrors] = useState({})
	const [requestError, setRequestError] = useState('')
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (!open) return
		setName(account?.name || '')
		setStatus(account?.status || 'active')
		setErrors({})
		setRequestError('')
	}, [account, open])

	const handleSubmit = async (event) => {
		event.preventDefault()
		if (saving) return

		const validation = validateAvitoAccountInput({ name, status })
		setErrors(validation.errors)
		setRequestError('')
		if (!validation.valid) return

		setSaving(true)
		try {
			const saved = account
				? await updateAvitoAccount(account.id, validation.payload)
				: await createAvitoAccount(validation.payload)
			onSaved(saved)
		} catch (error) {
			setRequestError(getAvitoAccountErrorMessage(error))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
			<BoxForm onSubmit={handleSubmit}>
				<DialogTitle>
					{account ? 'Редактировать аккаунт Авито' : 'Добавить аккаунт Авито'}
				</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Alert severity="info">
							Аккаунт определяет кабинет выгрузки, контактные данные и контекст
							объявления. Подключение к API Авито не выполняется.
						</Alert>
						{requestError && <Alert severity="error">{requestError}</Alert>}
						<TextField
							autoFocus
							label="Название аккаунта"
							value={name}
							onChange={(event) => {
								setName(event.target.value)
								setErrors((current) => ({ ...current, name: undefined }))
							}}
							error={Boolean(errors.name)}
							helperText={errors.name || 'Например: Основной кабинет'}
							inputProps={{ maxLength: 100 }}
							disabled={saving}
							fullWidth
							required
						/>
						<FormControl error={Boolean(errors.status)} disabled={saving}>
							<FormLabel>Статус</FormLabel>
							<RadioGroup
								row
								value={status}
								onChange={(event) => setStatus(event.target.value)}>
								<FormControlLabel value="active" control={<Radio />} label="Активен" />
								<FormControlLabel
									value="disabled"
									control={<Radio />}
									label="Отключён"
								/>
							</RadioGroup>
							{errors.status && (
								<Typography variant="caption" color="error">
									{errors.status}
								</Typography>
							)}
						</FormControl>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} disabled={saving}>
						Отмена
					</Button>
					<Button type="submit" variant="contained" disabled={saving}>
						{saving ? <CircularProgress size={22} /> : 'Сохранить'}
					</Button>
				</DialogActions>
			</BoxForm>
		</Dialog>
	)
}

function BoxForm({ children, onSubmit }) {
	return (
		<form noValidate onSubmit={onSubmit}>
			{children}
		</form>
	)
}

export default AvitoAccountFormDialog
